import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import Color from "color";

import { css, cx, styled } from "simplydo/react/styled";
import { mobileOnly, tabletUp, useTheme } from "simplydo/react/theme";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { H1 } from "simplydo/react/ui/header";
import { Loader } from "simplydo/react/ui/loader";

import api from "api";
import { useAppSelector } from "store";
import { useDocumentLang } from "utils/useDocumentLang";
import util from "utils/utils";

import OrgTagChooser from "components/lib/Choosers/Tags/OrgTagChooser";
import AgreeToTerms from "components/lib/Entry/AgreeToTerms";
import InitialProfileSetup from "components/lib/Entry/InitialProfileSetup";
import VerifyEmail from "components/lib/Entry/VerifyEmail";

import { StyledDashboardContent } from "./AppStyledDashboard";
import AppWrap from "./AppWrap";
import AppRouting from "./app/AppRouting";
import CustomOnboarding from "./lib/CustomOnboarding";
import CompleteVerifyEmail from "./lib/Entry/CompleteVerifyEmail";
import CreateBusinessProfile from "./lib/Entry/CreateBusinessProfile";
import EnforceMFA from "./lib/Entry/EnforceMFA";

const transitionSegmentClass = css`
  opacity: 0;
  animation-duration: 0.7s;
  animation-fill-mode: forwards;
  @keyframes slideInFull {
    0% {
      margin-top: -20%;
      opacity: 0;
    }
    100% {
      margin-top: 0;
      opacity: 1;
    }
  }
  @keyframes slideInOffset {
    0% {
      margin-top: -20%;
      opacity: 0;
    }
    100% {
      margin-top: -5%;
      opacity: 1;
    }
  }
  &.slide-full {
    animation-name: slideInFull;
  }
  &.slide-offset {
    animation-name: slideInOffset;
  }
`;

const MaxContent = styled.div`
  position: relative;
  min-height: 40px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 2px;

  &.is-scrolling {
    max-height: 70vh;
  }

  ${mobileOnly} {
    padding-right: 0px;
  }

  ${tabletUp} {
    padding-right: 10px;
  }
`;

// Better visual gradient, source https://css-tricks.com/easing-linear-gradients/
const scrimGradient = (colorStr: string) => {
  const color = Color(colorStr);

  return `radial-gradient(
    80vw 80vh at 100% 100%,
    ${color.rgb()} 0%,
    ${color.alpha(0.738).rgb()} 19%,
    ${color.alpha(0.541).rgb()} 34%,
    ${color.alpha(0.382).rgb()} 47%,
    ${color.alpha(0.278).rgb()} 56.5%,
    ${color.alpha(0.194).rgb()} 65%,
    ${color.alpha(0.126).rgb()} 73%,
    ${color.alpha(0.075).rgb()} 80.2%,
    ${color.alpha(0.042).rgb()} 86.1%,
    ${color.alpha(0.021).rgb()} 91%,
    ${color.alpha(0.008).rgb()} 95.2%,
    ${color.alpha(0.002).rgb()} 98.2%,
    transparent 100%
  )`;
};

type StyledDashboardProps = {
  content: React.ReactNode;
  hideName?: boolean;
  fluid?: boolean;
  excludeWelcomeText?: boolean;
  scrolling?: boolean;
  padded?: boolean | "very";
  backgroundColor?: string;
};

export const StyledDashboard = ({
  content,
  hideName,
  fluid,
  excludeWelcomeText,
  scrolling = true,
  backgroundColor,
}: StyledDashboardProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const urlOrganisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const organisation = user?.ownerOrganisation || urlOrganisation;
  const theme = useTheme();

  return (
    <StyledDashboardContent
      style={{
        position: "relative",
        paddingTop: scrolling ? 0 : 80,
        paddingBottom: scrolling ? 0 : 80,
      }}
    >
      <div
        style={{
          transition: "opacity 0.3s ease",
          opacity: organisation ? 1 : 0,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: scrimGradient(organisation?.primaryColour || "#fff"),
        }}
      />
      {backgroundColor ? (
        <div
          style={{
            transition: "opacity 0.3s ease",
            opacity: organisation ? 0 : 1,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: scrimGradient(backgroundColor),
          }}
        />
      ) : null}
      <Container
        fluid
        style={{ alignItems: "center", justifyContent: "center" }}
      >
        <div
          style={{
            width: theme.sizes.isMobile ? "100%" : fluid ? "62.5%" : "37.5%",
            minWidth: theme.sizes.isMobile ? undefined : 600,
            maxWidth: "100%",
          }}
        >
          <Card
            elevation="large"
            className={cx(
              transitionSegmentClass,
              theme.sizes.isMobile || fluid ? "slide-full" : "slide-offset",
            )}
            fluid
          >
            <CardContent>
              {!excludeWelcomeText ? (
                <>
                  <H1>
                    <span
                      role="img"
                      aria-label="Wave"
                      style={{ marginRight: 12 }}
                    >
                      👋
                    </span>
                    {!hideName && user?.profile?.firstName
                      ? t("entry.enrol.welcome_name", {
                          firstName: user?.profile?.firstName,
                        })
                      : t("entry.enrol.welcome")}
                  </H1>
                  <Divider />
                </>
              ) : null}
              <MaxContent className={cx(scrolling && "is-scrolling")}>
                {content}
              </MaxContent>
            </CardContent>
          </Card>
        </div>
      </Container>
    </StyledDashboardContent>
  );
};

const AppOnboarding = () => {
  useDocumentLang();
  const user = useAppSelector((state) => state.user);
  const location = useLocation();

  const [resumingSession, setResumingSession] = useState(true);
  const userHasVerifiedEmail = util.hasVerifiedEmail(
    user,
    user?.ownerOrganisation,
  );
  const userHasSetUpMFA = util.hasSetUpMFA(user, user?.ownerOrganisation);

  useEffect(() => {
    const now = new Date();
    const ensureElapsed = () => {
      const later = new Date();
      const diff = later.getTime() - now.getTime();

      setTimeout(
        () => {
          setResumingSession(false);
        },
        Math.max(0, 500 - diff),
      );
    };
    setResumingSession(true);
    api.auth.resumeSession(ensureElapsed, ensureElapsed);
  }, []);

  const shouldShowTagChooser = useMemo(
    () => util.shouldShowInterestsChooser(user),
    [user],
  );

  const onboardScreen = useMemo(() => {
    if (resumingSession) {
      return {
        done: false,
        content: (
          <AppWrap
            key="load"
            hideName
            content={<Loader size="large" data-label="onboarding-loader" />}
          />
        ),
      };
    }
    if (location.pathname === "/verify-email") {
      return {
        done: false,
        content: (
          <AppWrap
            key="complete-verify"
            hideName
            excludeWelcomeText
            content={<CompleteVerifyEmail />}
          />
        ),
      };
    }
    if (user && !util.hasAgreedTerms(user)) {
      return {
        done: false,
        content: <AppWrap key="terms" content={<AgreeToTerms />} />,
      };
    }
    if (user && !userHasVerifiedEmail) {
      return {
        done: false,
        content: <AppWrap key="verify" content={<VerifyEmail />} />,
      };
    }
    if (user && !userHasSetUpMFA) {
      return {
        done: false,
        content: <AppWrap key="mfa" content={<EnforceMFA />} />,
      };
    }
    if (user && (!user.profile?.firstName || !user.profile?.lastName)) {
      return {
        done: false,
        content: <AppWrap key="profile" content={<InitialProfileSetup />} />,
      };
    }
    if (shouldShowTagChooser) {
      return {
        done: false,
        content: <AppWrap key="tags" fluid content={<OrgTagChooser />} />,
      };
    }
    if (user?.groupRequiringProfile) {
      return {
        done: false,
        content: (
          <AppWrap key="bizprofile" fluid content={<CreateBusinessProfile />} />
        ),
      };
    }
    if (user && !user.hasCompletedOrganisationOnboarding) {
      return {
        done: false,
        content: <AppWrap key="onboarding" content={<CustomOnboarding />} />,
      };
    }
    return {
      done: true,
      content: (
        <AppWrap
          key="load"
          hideName
          content={<Loader size="large" data-label="onboarding-loader" />}
        />
      ),
    };
  }, [
    user,
    resumingSession,
    shouldShowTagChooser,
    userHasVerifiedEmail,
    userHasSetUpMFA,
    location.pathname,
  ]);

  const visibilityDelayMs = 200;
  const fadeOutDurationMs = 500;
  const [onboardingVisible, setOnboardingVisible] = useState(true);
  const [onboardingMounted, setOnboardingMounted] = useState(true);
  useEffect(() => {
    if (onboardScreen.done) {
      const vTimeout = setTimeout(() => {
        setOnboardingVisible(false);
      }, visibilityDelayMs);

      const oTimeout = setTimeout(() => {
        setOnboardingMounted(false);
      }, visibilityDelayMs + fadeOutDurationMs);

      return () => {
        clearTimeout(vTimeout);
        clearTimeout(oTimeout);
      };
    } else {
      setOnboardingVisible(true);
      setOnboardingMounted(true);
    }
  }, [onboardScreen.done]);

  return (
    <>
      {onboardingMounted ? (
        <div
          data-label="onboarding-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            backgroundColor: "#fff",
            opacity: onboardingVisible ? 1 : 0,
            transition: `opacity ${fadeOutDurationMs / 1000}s`,
          }}
        >
          {onboardScreen.content}
        </div>
      ) : null}
      {onboardScreen.done ? <AppRouting /> : null}
    </>
  );
};

export default AppOnboarding;
